/**
 * Cloud live player for the shared Alert Hexagon page.
 *
 * HexStreamProvider is now a thin adapter over the app-level NodeStreamProvider
 * (mounted in Layout) — it no longer owns a NodeConnectionManager. The single
 * shared connection is kept warm for the whole session and reused across pages,
 * so opening the Hexagon page (or clicking a hexagon) only adds a track over the
 * existing WS+PC. HexLivePlayer is the injected PlayerComponent and is unchanged:
 * it ref-counts a camera subscription (shared ref-count + linger now live in the
 * app provider) and renders LiveStreamPlayer for its stream.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useNode } from '../../contexts/NodeContext';
import { type NodeConnectionManager } from '../../services/node-connection-manager';
import { useNodeStream } from '../../contexts/NodeStreamContext';
import LiveStreamPlayer from '../LiveStreamPlayer';

interface Controls {
  nodeId: string | null;
  add: (cameraId: string, label?: string) => void;
  remove: (cameraId: string) => void;
  manager: () => NodeConnectionManager | null;
}

const ControlsCtx = createContext<Controls | null>(null);

export function HexStreamProvider({ children }: { children: ReactNode }) {
  const { selectedNode } = useNode();
  const nodeId = selectedNode?.nodeId ?? null;
  const nodeName = selectedNode?.name ?? '';
  const { subscribe, unsubscribe, manager } = useNodeStream();

  const controls = useMemo<Controls>(
    () => ({
      nodeId,
      add: (cameraId: string, label?: string) => {
        if (nodeId) subscribe(nodeId, nodeName, cameraId, label);
      },
      remove: (cameraId: string) => {
        if (nodeId) unsubscribe(nodeId, cameraId);
      },
      manager,
    }),
    [nodeId, nodeName, subscribe, unsubscribe, manager],
  );

  return <ControlsCtx.Provider value={controls}>{children}</ControlsCtx.Provider>;
}

interface HexLivePlayerProps {
  cameraId: string;
  label?: string;
  className?: string;
  showOverlay?: boolean;
  /** Fired when the live video actually starts playing (see HexPlayerComponent). */
  onStreamStart?: () => void;
}

export default function HexLivePlayer({ cameraId, label, className, onStreamStart }: HexLivePlayerProps) {
  const controls = useContext(ControlsCtx);
  const { streams, states } = useNodeStream();

  useEffect(() => {
    if (!controls) return;
    controls.add(cameraId, label);
    return () => controls.remove(cameraId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, controls]);

  if (!controls || !controls.nodeId) return <div className={className} />;
  const key = `${controls.nodeId}:${cameraId}`;
  return (
    <LiveStreamPlayer
      stream={streams.get(key) || null}
      connectionState={states.get(key) || 'connecting'}
      label={label}
      className={className}
      onStalled={() => controls.manager()?.handleCameraStall(controls.nodeId as string, cameraId)}
      onPlaying={() => {
        controls.manager()?.handleCameraPlaying(controls.nodeId as string, cameraId);
        onStreamStart?.();
      }}
      onRetry={() => controls.manager()?.retryCamera(controls.nodeId as string, cameraId)}
    />
  );
}
